import type { MaximumMortgageWizardFormValues } from "../types/MaximumMortgageWizard";

/**
 * Filters the wizard form values to include only relevant fields
 * based on the conditional logic from the wizard slides.
 *
 * ⚠️ IMPORTANT: When modifying the Maximum Mortgage Wizard flow or adding/removing
 * conditional fields in any slide component, this filter function MUST be updated
 * to reflect those changes. This ensures only relevant data is submitted.
 *
 * Related files that may require filter updates:
 * - MaximumMortgageWizardProvider.tsx (slideOrder logic)
 * - IncomeSlide.tsx
 * - PurposeSlide.tsx
 * - NewHouseSlide.tsx
 * - CurrentHouseSlide.tsx
 * - FinancialObligationsSlide.tsx
 *
 * @param formValues - The complete form values from the wizard
 * @returns Filtered form values with only relevant fields
 */
export const filterMaximumMortgageWizardValues = (
  formValues: Partial<MaximumMortgageWizardFormValues>,
): Partial<MaximumMortgageWizardFormValues> => {
  const filtered: Partial<MaximumMortgageWizardFormValues> = {
    // Income slide - always included
    income: formValues.income,
    age: formValues.age,
    hasPartner: formValues.hasPartner,
  };

  // Partner fields - only if hasPartner is true
  if (formValues.hasPartner === true) {
    filtered.partnerIncome = formValues.partnerIncome;
    filtered.partnerAge = formValues.partnerAge;
  }

  // Purpose slide - always included
  filtered.purpose = formValues.purpose;

  // Purpose-specific fields for "finance"
  if (formValues.purpose === "finance") {
    filtered.houseInMind = formValues.houseInMind;

    // House progress and details - only if house in mind is existing or newly-build
    if (
      formValues.houseInMind === "existing" ||
      formValues.houseInMind === "newly-build"
    ) {
      filtered.houseProgress = formValues.houseProgress;

      // Price and energy label - only if house progress is set
      if (formValues.houseProgress) {
        filtered.newHousePrice = formValues.newHousePrice;
        filtered.newHouseEnergyLabel = formValues.newHouseEnergyLabel;
      }
    }

    // walterAddressObjectID - only for existing houses (not newly-build)
    if (formValues.houseInMind === "existing") {
      filtered.walterAddressObjectID = formValues.walterAddressObjectID;
    }

    // Current house slide fields - only for finance purpose
    filtered.hasCurrentHouse = formValues.hasCurrentHouse;

    // Current house details - only if hasCurrentHouse is true
    if (formValues.hasCurrentHouse === true) {
      filtered.currentHouseSellingPrice = formValues.currentHouseSellingPrice;
      filtered.currentHouseMortgageDebt = formValues.currentHouseMortgageDebt;
      filtered.currentHouseMortgageLender =
        formValues.currentHouseMortgageLender;
    }
  }

  // Purpose-specific fields for "refinance" or "renovate"
  if (formValues.purpose === "refinance" || formValues.purpose === "renovate") {
    filtered.currentHouseMarketValue = formValues.currentHouseMarketValue;
    filtered.currentHouseEnergyLabel = formValues.currentHouseEnergyLabel;
    filtered.currentHouseMortgageDebt = formValues.currentHouseMortgageDebt;
    filtered.currentHouseMortgageLender = formValues.currentHouseMortgageLender;
  }

  // Financial obligations - always included
  filtered.hasLoans = formValues.hasLoans;
  if (formValues.hasLoans === true) {
    filtered.loans = formValues.loans;
  }

  filtered.hasStudentDebt = formValues.hasStudentDebt;
  if (formValues.hasStudentDebt === true) {
    filtered.studentDebt = formValues.studentDebt;
  }

  filtered.hasPrivateLease = formValues.hasPrivateLease;
  if (formValues.hasPrivateLease === true) {
    filtered.privateLease = formValues.privateLease;
  }

  // Own capital - included for finance and refinance, not renovate
  if (formValues.ownCapital && formValues.purpose !== "renovate") {
    filtered.ownCapital = formValues.ownCapital;
  }

  return filtered;
};
